import * as React from 'react';
import { Button } from '@/hen-ui/atomics/Button';
import { Text, type TextProps } from '@/hen-ui/atomics/Text';
import { Flex } from '@/hen-ui/composites/Flex';
import { Tooltip } from '@/hen-ui/composites/Tooltip';
import type { ComparisonRowLabelTooltipProps } from '../types';

export type ComparisonRowLabelProps = ComparisonRowLabelTooltipProps & {
  label: string;
  textVariant?: TextProps['variant'];
  textColor?: TextProps['color'];
  textWeight?: TextProps['weight'];
};

/**
 * Comparison row label with optional info-icon help.
 *
 * Same contract as KPIBanner per-tile `labelTooltip*`: when `labelTooltip` is
 * non-empty, render hen-ui `Tooltip` + `iconOnly`/`flush` Button to the right
 * of the label; otherwise render the label alone.
 *
 * Layout uses Flex tokens (`gap`, `width`, `align`) rather than raw utility
 * stacks. `min-w-0` / `flex-1` on the label text are required so the sticky
 * label column can wrap without the trigger overflowing into value cells.
 */
export const ComparisonRowLabel: React.FC<ComparisonRowLabelProps> = ({
  label,
  labelTooltip,
  labelTooltipSide = 'top',
  labelTooltipFormat,
  labelTooltipTrigger,
  textVariant = 'l1',
  textColor = 'subtext',
  textWeight,
}) => {
  const trimmedTooltip = labelTooltip?.trim();

  const labelText = (
    <Text
      variant={textVariant}
      color={textColor}
      weight={textWeight}
      tag='span'
      className={trimmedTooltip ? 'min-w-0 flex-1' : undefined}
    >
      {label}
    </Text>
  );

  if (!trimmedTooltip) {
    return labelText;
  }

  return (
    <Flex
      direction='row'
      align='start'
      gap='min'
      width='full'
      className='min-w-0'
    >
      {labelText}
      <Tooltip
        content={trimmedTooltip}
        side={labelTooltipSide}
        contentFormat={labelTooltipFormat}
        trigger={labelTooltipTrigger}
      >
        <Button
          type='button'
          variant='iconOnly'
          size='flush'
          iconBefore='info'
          aria-label={`Info about ${label}`}
        />
      </Tooltip>
    </Flex>
  );
};

ComparisonRowLabel.displayName = 'ComparisonRowLabel';
